import { useT } from '../context/I18nContext';
import { useEffect, useState, useCallback } from 'react';
import { AdHocFeeDialog } from '../components/AdHocFeeDialog';
import { api, ApiError, openDocument } from '../lib/api';
import type { InvoiceView, PagedResultInv, InstalmentPlanView, ReversalAdviceView } from '../lib/fees-types';
import { WHAT_HAPPENED } from '../lib/fees-types';
import { approvalsApi } from '../lib/approvalsApi';
import { formatPaise, formatDate, toPaise } from '../lib/money';
import { StatusPill } from '../components/ui';
import { DataTable, type Column } from '../components/DataTable';
import { ModuleStrip } from '../components/ModuleStrip';

const STATUSES = ['All', 'Pending', 'PartiallyPaid', 'Paid', 'Cancelled'];

/**
 * The columns.
 *
 * ⚠ `value` matters as much as `cell` — it is what DataTable sorts and searches on, and what a
 * mobile card uses as its label. A column with a rich `cell` and no `value` sorts by nothing.
 */
const COLUMNS: Column<InvoiceView>[] = [
  {
    id: 'number', header: 'Invoice', sortable: true,
    value: (i) => i.number,
    cell: (i) => <span className="mono">{i.number}</span>,
  },
  {
    id: 'student', header: 'Student', sortable: true,
    primary: true,                       // the card title on a phone
    value: (i) => i.studentName ?? '',
    cell: (i) => (
      <>
        <div style={{ fontWeight: 500 }}>{i.studentName ?? '—'}</div>
        {/* Admission number and class beneath, because two children called Aditya in one school
            is not unusual — the demo data alone has three. */}
        {(i.admissionNumber || i.classLabel) && (
          <div style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>
            {[i.admissionNumber, i.classLabel].filter(Boolean).join(' · ')}
          </div>
        )}
      </>
    ),
  },
  {
    id: 'year', header: 'Year', sortable: true, hideMd: true,
    value: (i) => i.academicYear,
    cell: (i) => i.academicYear,
  },
  {
    id: 'total', header: 'Total', align: 'right', sortable: true,
    // ⚠ Sorted on PAISE, not the formatted string. Sorting "₹1,00,000.00" as text puts ₹9 after
    // ₹80,000 — the classic money-sorting bug, and the one a finance clerk notices immediately.
    value: (i) => i.totalPaise,
    cell: (i) => <span className="mono">{formatPaise(i.totalPaise)}</span>,
  },
  {
    id: 'balance', header: 'Balance', align: 'right', sortable: true,
    value: (i) => i.balancePaise,
    cell: (i) => (
      <span className="mono" style={{ color: i.balancePaise > 0 ? 'var(--warn)' : 'var(--ok)' }}>
        {formatPaise(i.balancePaise)}
      </span>
    ),
  },
  {
    id: 'due', header: 'Due', sortable: true, hideSm: true,
    // ⚠ The raw ISO date sorts correctly; "23 Jul 2026" sorts alphabetically and puts April first.
    value: (i) => i.dueDate ?? '',
    cell: (i) => formatDate(i.dueDate),
  },
  {
    id: 'status', header: 'Status', sortable: true,
    value: (i) => i.status,
    cell: (i) => <StatusPill status={i.status} />,
  },
];

export function FeesPage() {
  const t = useT();
  const [data, setData] = useState<PagedResultInv | null>(null);
  const [status, setStatus] = useState('All');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<InvoiceView | null>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(25);
  const [adHoc, setAdHoc] = useState(false);

  const load = useCallback(async (st: string, q: string, p: number, sz: number) => {
    setLoading(true);
    try {
      // Server-side paging: a school with four hundred students has thousands of invoices, and
      // fetching them all to paginate in the browser is the version that dies in year two.
      const params = new URLSearchParams({ take: String(sz), skip: String((p - 1) * sz) });
      if (st !== 'All') params.set('status', st);
      if (q.trim()) params.set('search', q.trim());
      setData(await api.get<PagedResultInv>(`/api/fees/invoices?${params}`));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(status, search, page, size); }, [status, search, page, size, load]);

  // A new filter or search resets to page one — otherwise a clerk on page 4 filters to "Paid",
  // gets an empty screen, and concludes there are no paid invoices.
  useEffect(() => { setPage(1); }, [status, search]);

  return (
    <div>
      <div className="page-head" style={{ marginBottom: 22 }}>
        <div>
          <div className="eyebrow" style={{ marginBottom: 6 }}>Finance</div>
          <h1 className="h-display" style={{ fontSize: 28 }}>{t('Fees')}</h1>
        </div>
        <div className="page-head-actions">
          {/* ⚠ ON THE FEES PAGE, not buried in a settings screen. "Add a cultural fee for the
              children going" is a thing an office does in the same minute it looks at the ledger. */}
          <button className="btn btn-accent" onClick={() => setAdHoc(true)}>+ One-off fee</button>
        </div>
      </div>

      <ModuleStrip module="fees" />

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {STATUSES.map((s) => (
          <button key={s} className="btn" onClick={() => setStatus(s)}
            style={{
              padding: '7px 14px',
              background: status === s ? 'var(--brand)' : 'var(--surface)',
              color: status === s ? '#fff' : 'var(--ink-2)',
              borderColor: status === s ? 'var(--brand)' : 'var(--line)',
            }}>
            {s === 'PartiallyPaid' ? 'Partly paid' : s}
          </button>
        ))}
      </div>

      <DataTable<InvoiceView>
        rows={loading ? null : (data?.items ?? [])}
        columns={COLUMNS}
        rowKey={(r) => r.id}

        search
        searchPlaceholder="Search by student name or admission number…"
        onSearch={setSearch}

        total={data?.total ?? 0}
        page={page}
        pageSize={size}
        onPage={(p, sz) => { setPage(p); setSize(sz); }}

        onRowClick={setSelected}
        empty={{
          title: 'No invoices in this view',
          body: search ? 'No student matched that search.' : 'Try a different status filter.',
        }}
      />

      {selected && (
        <InvoiceDrawer invoice={selected} onClose={() => setSelected(null)}
          onPaid={(updated) => { setSelected(updated); load(status, search, page, size); }} />
      )}

      {adHoc && (
        <AdHocFeeDialog onClose={() => setAdHoc(false)}
          onDone={() => load(status, search, page, size)} />
      )}
    </div>
  );
}

function InvoiceDrawer({ invoice, onClose, onPaid }: { invoice: InvoiceView; onClose: () => void; onPaid: (i: InvoiceView) => void }) {
  /**
   * Open the office copy of a receipt for one payment.
   *
   * ⚠ /api/fees/... not /api/portal/... — the staff route. Passing no family id makes the renderer
   * stamp it "Office Copy" instead of "Parent Copy", which is the distinction the school cares
   * about when they file one and hand the other over.
   */
  const openReceipt = async (invoiceId: string, paymentId: string) => {
    try { await openDocument(`/api/fees/invoices/${invoiceId}/receipt/${paymentId}`); }
    catch { alert('Could not open the receipt.'); }
  };

  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('Cash');
  const [reference, setReference] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ---- request a discount (Approval Engine) ----
  const [discAmount, setDiscAmount] = useState('');
  const [discName, setDiscName] = useState('');
  const [discReason, setDiscReason] = useState('');
  const [discBusy, setDiscBusy] = useState(false);
  const [discMsg, setDiscMsg] = useState<string | null>(null);

  const requestDiscount = async () => {
    setDiscMsg(null); setDiscBusy(true);
    try {
      const res = await approvalsApi.raiseFeeDiscount({
        invoiceId: invoice.id,
        discountPaise: toPaise(discAmount),
        requestedInName: discName.trim(),
        reason: discReason.trim() || null,
      });
      setDiscMsg(
        res.status === 'Approved'
          ? `Approved within your cap — ₹${(res.amountPaise / 100).toLocaleString('en-IN')} applied to this invoice.`
          : `Sent to management for approval. You'll be notified when it's decided.`,
      );
      setDiscAmount(''); setDiscName(''); setDiscReason('');
    } catch (e) {
      setDiscMsg(e instanceof ApiError ? e.message : 'Could not raise the discount.');
    } finally { setDiscBusy(false); }
  };

  const [took, setTook] = useState<string | null>(null);

  /**
   * Take whatever was actually handed over.
   *
   * ⚠ IT POSTS TO counter-payment, NOT payments, AND THAT IS THE FIX. RecordPayment refuses anything
   * above the outstanding balance — correctly — so a family handing ₹50,000 across the counter for a
   * ₹45,000 bill left a clerk holding ₹5,000 the software would not accept, and the honest options
   * were a second invoice invented on the spot or a drawer. This endpoint settles the bill and holds
   * the rest, in one act, because the family handed over one amount once.
   *
   * ⚠ THE SERVER'S SENTENCE IS WHAT GETS SHOWN AFTERWARDS, not one built here. The split below is a
   * preview so a clerk can see what is about to happen; the server recomputes it and its wording is
   * the record.
   */
  const record = async () => {
    setError(null); setBusy(true); setTook(null);
    try {
      const res = await api.post<{ toInvoicePaise: number; toCreditPaise: number; words: string;
        creditBalancePaise: number; invoice: InvoiceView }>(
        `/api/fees/invoices/${invoice.id}/counter-payment`, {
          handedOverPaise: toPaise(amount), method, reference: reference || null, reason: null,
        });
      onPaid(res.invoice);
      setTook(res.words);
      setAmount(''); setReference('');
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not record the payment.');
    } finally { setBusy(false); }
  };

  const canPay = invoice.balancePaise > 0 && invoice.status !== 'Cancelled';

  /**
   * ⚠ A PREVIEW ONLY, AND THE SERVER IS AUTHORITATIVE. This mirrors CounterPayment.Split, which is
   * domain logic living in two languages — normally the thing to avoid. It is here because a clerk
   * standing in front of a parent needs to see the split BEFORE pressing anything, and a round trip
   * per keystroke is not that. If the two ever disagree, the server's answer is the one that
   * happened, and it is the server's sentence that is shown afterwards.
   */
  const handed = amount ? toPaise(amount) : 0;
  const excess = Math.max(0, handed - invoice.balancePaise);

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(10,12,18,.45)', zIndex: 50, display: 'flex', justifyContent: 'flex-end' }}>
      <div onClick={(e) => e.stopPropagation()} className="card" style={{
        width: 460, maxWidth: '100%', height: '100%', borderRadius: 0, padding: 26, overflowY: 'auto',
      }}>
        <div className="eyebrow" style={{ marginBottom: 6 }}>Invoice</div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
          <h2 className="h-display mono" style={{ fontSize: 22 }}>{invoice.number}</h2>
          <StatusPill status={invoice.status} />
        </div>

        {/* lines */}
        <div className="card" style={{ padding: 16, marginBottom: 16, background: 'var(--surface-2)' }}>
          {invoice.lines.map((l, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', fontSize: 14 }}>
              <span style={{ color: 'var(--ink-2)' }}>{l.label}</span>
              <span className="mono">{formatPaise(l.amountPaise)}</span>
            </div>
          ))}
          <div style={{ borderTop: '1px solid var(--line)', marginTop: 8, paddingTop: 10, display: 'flex', justifyContent: 'space-between', fontWeight: 600 }}>
            <span>Total</span><span className="mono">{formatPaise(invoice.totalPaise)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, color: 'var(--ok)' }}>
            <span>Paid</span><span className="mono">{formatPaise(invoice.paidPaise)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 2, fontWeight: 600, color: invoice.balancePaise > 0 ? 'var(--warn)' : 'var(--ok)' }}>
            <span>Balance</span><span className="mono">{formatPaise(invoice.balancePaise)}</span>
          </div>
        </div>

        {/* ⚠ MONEY ALREADY HELD COMES BEFORE ASKING FOR MORE. A parent standing at a counter being
            asked for ₹35,000 while the school is holding ₹5,000 of theirs is the whole reason this
            panel is above the payment box and not below it. */}
        {canPay && <CreditPanel invoice={invoice} onApplied={onPaid} />}

        {/* record payment */}
        {canPay ? (
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontWeight: 600, marginBottom: 12 }}>Record a payment</div>
            {error && <div className="pill pill-danger" style={{ padding: '8px 12px', borderRadius: 'var(--r-md)', justifyContent: 'flex-start', marginBottom: 12 }}>{error}</div>}
            <label className="label">Amount (₹)</label>
            <input className="input mono" inputMode="decimal" placeholder="0.00" value={amount}
              onChange={(e) => setAmount(e.target.value)} style={{ marginBottom: 12 }} />
            <label className="label">Method</label>
            <select className="input" value={method} onChange={(e) => setMethod(e.target.value)} style={{ marginBottom: 12 }}>
              <option>Cash</option><option>Online</option><option>Cheque</option><option>BankTransfer</option>
            </select>
            <label className="label">Reference (optional)</label>
            <input className="input" placeholder="Txn / cheque no." value={reference}
              onChange={(e) => setReference(e.target.value)} style={{ marginBottom: 16 }} />
            {/* ⚠ THE SPLIT IS SHOWN BEFORE THE BUTTON IS PRESSED. A clerk who takes ₹50,000 and
                then discovers where ₹5,000 went has already told the parent something. */}
            {excess > 0 && (
              <div data-testid="counter-split" className="card" style={{ padding: '11px 13px',
                marginBottom: 12, background: 'var(--surface-2)', fontSize: 13.5, lineHeight: 1.55 }}>
                <b>{formatPaise(invoice.balancePaise)}</b> clears this bill and{' '}
                <b>{formatPaise(excess)}</b> is held against their next one.
              </div>
            )}
            <button className="btn btn-accent" data-testid="take-payment"
              style={{ width: '100%', justifyContent: 'center' }}
              disabled={busy || !amount || toPaise(amount) <= 0} onClick={record}>
              {busy ? 'Recording…' : `Take ${amount ? formatPaise(toPaise(amount)) : 'payment'}`}
            </button>
            {/* ⚠ THE SERVER'S OWN WORDS. A clerk reads this out to the parent, so it must be the
                sentence describing what actually happened rather than one composed in the browser. */}
            {took && (
              <div data-testid="counter-result" style={{ marginTop: 10, fontSize: 13.5,
                color: '#2F6B4F', lineHeight: 1.5 }}>{took}</div>
            )}
          </div>
        ) : (
          <div className="pill pill-ok" style={{ padding: '10px 14px', borderRadius: 'var(--r-md)', marginBottom: 20 }}>
            {invoice.status === 'Cancelled' ? 'This invoice was cancelled.' : 'Fully paid — nothing outstanding.'}
          </div>
        )}

        {/* split the balance into instalments */}
        {canPay && <SplitSection invoice={invoice} onSplit={onPaid} />}

        {/* ⚠ AVAILABLE EVEN ON A FULLY PAID INVOICE, unlike the payment box above it. A bounced
            cheque and a withdrawal both arrive AFTER the invoice reads as paid — gating this on an
            outstanding balance would hide it in exactly the state it is needed. */}
        {invoice.status !== 'Cancelled' && invoice.payments.some((p) => !p.isVoided) && (
          <ReversalSection invoice={invoice} onChanged={onPaid} />
        )}

        {/* request a discount — Approval Engine */}
        {invoice.status !== 'Cancelled' && (
          <div style={{ marginBottom: 20, borderTop: '1px solid var(--line)', paddingTop: 16 }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>Request a discount</div>
            <div style={{ fontSize: 12, color: 'var(--ink-2)', marginBottom: 12 }}>
              Within your approved cap it applies at once. Above it, it goes to management to approve on their phone.
            </div>
            {discMsg && <div className="pill" style={{ padding: '8px 12px', borderRadius: 'var(--r-md)', justifyContent: 'flex-start', marginBottom: 12, fontSize: 13 }}>{discMsg}</div>}
            <label className="label">Discount amount (₹)</label>
            <input className="input mono" inputMode="decimal" placeholder="0.00" value={discAmount}
              onChange={(e) => setDiscAmount(e.target.value)} style={{ marginBottom: 12 }} />
            <label className="label">In whose name</label>
            <input className="input" placeholder="e.g. Sri … / trustee / parent name" value={discName}
              onChange={(e) => setDiscName(e.target.value)} style={{ marginBottom: 12 }} />
            <label className="label">Reason (optional)</label>
            <input className="input" placeholder="Sibling concession, staff ward, …" value={discReason}
              onChange={(e) => setDiscReason(e.target.value)} style={{ marginBottom: 16 }} />
            <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }}
              disabled={discBusy || !discAmount || toPaise(discAmount) <= 0 || !discName.trim()}
              onClick={requestDiscount}>
              {discBusy ? 'Submitting…' : 'Request discount'}
            </button>
          </div>
        )}

        {/* payment history */}
        {invoice.payments.length > 0 && (
          <div>
            <div className="label">Payment history</div>
            {invoice.payments.map((p) => (
              <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 0', borderBottom: '1px solid var(--line-2)', opacity: p.isVoided ? .5 : 1 }}>
                <div>
                  <div className="mono" style={{ fontSize: 14, textDecoration: p.isVoided ? 'line-through' : 'none' }}>{formatPaise(p.amountPaise)}</div>
                  <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>{p.method} · {formatDate(p.recordedAt)} · {p.recordedBy}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  {p.reference && <span className="mono" style={{ fontSize: 12, color: 'var(--ink-3)' }}>{p.reference}</span>}
                  {/* ⚠ THE COUNTER RECEIPT. A parent pays at the desk and expects a slip before
                      they walk away — they are not logging into a portal standing there. Voided
                      payments get NO button: a reversed transaction must never have printable
                      proof in circulation. */}
                  {!p.isVoided && (
                    <button className="btn" style={{ padding: '4px 10px', fontSize: 12 }}
                      onClick={() => void openReceipt(invoice.id, p.id)}>
                      Receipt
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        <button className="btn" style={{ width: '100%', justifyContent: 'center', marginTop: 20 }} onClick={onClose}>Close</button>
      </div>
    </div>
  );
}

/**
 * ═══ SOMETHING IS WRONG WITH A PAYMENT ═══
 *
 * ⚠ IT ASKS WHAT HAPPENED BEFORE IT OFFERS A BUTTON, and that ordering is the whole feature. "Void"
 * and "Refund" side by side is a coin toss made by whoever is on the desk, and the wrong side of
 * that toss is invisible for months: voiding a payment that should have been refunded gives a ledger
 * that balances perfectly against a bank account that is short. Nobody spots it until an audit, and
 * by then there are forty of them.
 *
 * ⚠ A BOUNCED CHEQUE IS THE ONE PEOPLE GET WRONG. Refunding it sends real money out of the building
 * against a payment that never arrived — the school quietly pays the fee on the family's behalf.
 */
function ReversalSection({ invoice, onChanged }: { invoice: InvoiceView; onChanged: (i: InvoiceView) => void }) {
  const [what, setWhat] = useState('');
  const [advice, setAdvice] = useState<ReversalAdviceView | null>(null);
  const [reason, setReason] = useState('');
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('BankTransfer');
  const [reference, setReference] = useState('');
  const [paymentId, setPaymentId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const live = invoice.payments.filter((p) => !p.isVoided);
  const refunds = invoice.refunds ?? [];

  const ask = async (value: string) => {
    setWhat(value); setAdvice(null); setError(null);
    if (!value) return;
    setBusy(true);
    try {
      setAdvice(await api.get<ReversalAdviceView>(
        `/api/fees/invoices/${invoice.id}/reversal-advice?what=${value}`));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not work out what to do.');
    } finally { setBusy(false); }
  };

  const doVoid = async () => {
    setBusy(true); setError(null);
    try {
      onChanged(await api.post<InvoiceView>(
        `/api/fees/invoices/${invoice.id}/payments/${paymentId}/void`, { reason }));
      setWhat(''); setAdvice(null); setReason(''); setPaymentId('');
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not void that payment.');
    } finally { setBusy(false); }
  };

  const doRefund = async () => {
    setBusy(true); setError(null);
    try {
      onChanged(await api.post<InvoiceView>(`/api/fees/invoices/${invoice.id}/refunds`, {
        amountPaise: toPaise(amount), method, reference: reference || null, reason,
      }));
      setWhat(''); setAdvice(null); setReason(''); setAmount(''); setReference('');
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not record that refund.');
    } finally { setBusy(false); }
  };

  return (
    <div style={{ marginBottom: 20, borderTop: '1px solid var(--line)', paddingTop: 16 }}
      data-testid="reversal-section">
      <div style={{ fontWeight: 600, marginBottom: 4 }}>Something is wrong with a payment</div>

      {/* ⚠ REFUNDS ARE LISTED SEPARATELY FROM PAYMENTS, ALWAYS. Netting them into one list would
          make a refund indistinguishable from a void on the one screen where somebody is choosing
          between them — and a bank statement carries two entries for a refund and none for a void. */}
      {refunds.length > 0 && (
        <div data-testid="refunds" style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 12, color: 'var(--ink-2)', marginBottom: 6 }}>
            Sent back to the family
          </div>
          {refunds.map((r) => (
            <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between',
              padding: '5px 0', fontSize: 13.5, gap: 12 }}>
              <span style={{ color: 'var(--ink-2)', minWidth: 0 }}>
                {r.method}{r.reference ? ` · ${r.reference}` : ''} · {formatDate(r.recordedAt)}
                <span style={{ display: 'block', fontSize: 11.5, color: 'var(--ink-3)' }}>
                  {r.reason} — {r.recordedBy}
                </span>
              </span>
              <span className="mono" style={{ flex: '0 0 auto' }}>−{formatPaise(r.amountPaise)}</span>
            </div>
          ))}
        </div>
      )}

      {error && (
        <div className="pill pill-danger" data-testid="reversal-error"
          style={{ padding: '8px 12px', borderRadius: 'var(--r-md)', justifyContent: 'flex-start',
            marginBottom: 12, fontSize: 13 }}>{error}</div>
      )}

      <label className="label">What happened?</label>
      <select className="input" value={what} data-testid="what-happened"
        onChange={(e) => void ask(e.target.value)} style={{ marginBottom: 12 }}>
        <option value="">Choose…</option>
        {WHAT_HAPPENED.map((w) => <option key={w.value} value={w.value}>{w.label}</option>)}
      </select>

      {advice && (
        <div data-testid="advice" className="card"
          style={{ padding: 14, marginBottom: 12, background: 'var(--surface-2)' }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }} data-testid="advice-do">{advice.do}</div>
          <div style={{ fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.55 }}>{advice.because}</div>
          {/* ⚠ THE WARNING IS THE MISTAKE, NAMED. "Do NOT refund a bounced cheque" is worth more
              than any amount of explanation about what a void is. */}
          {advice.warning && (
            <div data-testid="advice-warning" style={{ fontSize: 13, marginTop: 8, lineHeight: 1.55,
              color: 'var(--danger, #B3372E)', fontWeight: 500 }}>
              {advice.warning}
            </div>
          )}
          {advice.scheduleExplanation && (
            <div data-testid="advice-schedule" style={{ fontSize: 13, marginTop: 8, lineHeight: 1.55,
              color: 'var(--ink-2)' }}>
              {advice.scheduleExplanation}
            </div>
          )}
        </div>
      )}

      {/* ⚠ ONLY THE ACTION THE ADVICE POINTS AT IS OFFERED. Showing both and letting somebody pick
          is the coin toss this section exists to remove. */}
      {advice?.kind === 'VoidPayment' && (
        <div data-testid="void-form">
          <label className="label">Which payment</label>
          <select className="input" value={paymentId} data-testid="void-payment"
            onChange={(e) => setPaymentId(e.target.value)} style={{ marginBottom: 12 }}>
            <option value="">Choose…</option>
            {live.map((p) => (
              <option key={p.id} value={p.id}>
                {formatPaise(p.amountPaise)} · {p.method}{p.reference ? ` · ${p.reference}` : ''} · {formatDate(p.recordedAt)}
              </option>
            ))}
          </select>
          <label className="label">Why</label>
          <input className="input" value={reason} data-testid="void-reason"
            placeholder="Cheque returned unpaid by the bank"
            onChange={(e) => setReason(e.target.value)} style={{ marginBottom: 12 }} />
          <button className="btn btn-accent" data-testid="void-apply"
            style={{ width: '100%', justifyContent: 'center' }}
            disabled={busy || !paymentId || !reason.trim()} onClick={() => void doVoid()}>
            {busy ? 'Working…' : 'Void that payment'}
          </button>
        </div>
      )}

      {advice?.kind === 'Refund' && (
        <div data-testid="refund-form">
          {/* ⚠ THE CEILING IS STATED BEFORE THE BOX, not enforced after it. A clerk who types a
              number and is then told it is too large has already promised it to a parent. */}
          <div style={{ fontSize: 12, color: 'var(--ink-2)', marginBottom: 10 }}>
            {formatPaise(advice.refundablePaise)} was received and not yet sent back
            {advice.alreadyRefundedPaise > 0 && <> ({formatPaise(advice.alreadyRefundedPaise)} already refunded)</>}.
          </div>
          <label className="label">Amount (₹)</label>
          <input className="input mono" inputMode="decimal" value={amount} data-testid="refund-amount"
            placeholder="0.00" onChange={(e) => setAmount(e.target.value)} style={{ marginBottom: 12 }} />
          <label className="label">How it went back</label>
          <select className="input" value={method} data-testid="refund-method"
            onChange={(e) => setMethod(e.target.value)} style={{ marginBottom: 12 }}>
            <option value="BankTransfer">Bank transfer</option>
            <option value="Cheque">Cheque</option>
            <option value="Cash">Cash</option>
            <option value="Online">Online</option>
          </select>
          {/* ⚠ SOMEBODY HAS TO FIND THIS ON A STATEMENT IN THREE MONTHS. */}
          <label className="label">Reference</label>
          <input className="input" value={reference} data-testid="refund-reference"
            placeholder="NEFT / cheque no." onChange={(e) => setReference(e.target.value)}
            style={{ marginBottom: 12 }} />
          <label className="label">Why</label>
          <input className="input" value={reason} data-testid="refund-reason"
            placeholder="Withdrew in July — UGC schedule"
            onChange={(e) => setReason(e.target.value)} style={{ marginBottom: 12 }} />
          <button className="btn btn-accent" data-testid="refund-apply"
            style={{ width: '100%', justifyContent: 'center' }}
            disabled={busy || !amount || toPaise(amount) <= 0 || !reason.trim()}
            onClick={() => void doRefund()}>
            {busy ? 'Working…' : `Refund ${amount ? formatPaise(toPaise(amount)) : ''}`}
          </button>
        </div>
      )}

      {/* ⚠ THE TWO ANSWERS WITH NO BUTTON HERE STILL GET AN ANSWER. A section that goes quiet when
          the right move is something else reads as broken, and the clerk falls back to guessing. */}
      {(advice?.kind === 'CarryCredit' || advice?.kind === 'CancelInvoice') && (
        <div data-testid="no-action" style={{ fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.55 }}>
          {advice.kind === 'CarryCredit'
            ? 'Nothing to do on this invoice — set it against the next bill when you raise it.'
            : 'Cancel this invoice from the actions above. Nothing was paid, so no money moves.'}
        </div>
      )}
    </div>
  );
}

/**
 * ═══ SPLIT THE BALANCE INTO INSTALMENTS ═══
 *
 * The endpoints have been live since the instalment slice and nothing called them, so a school that
 * had set a policy of "three, one every three months" still had a counter clerk splitting invoices
 * on paper.
 *
 * ⚠ THE PREVIEW IS NOT OPTIONAL. The server refuses a count above the school's policy, refuses a
 * balance too small to divide, and rounds the last instalment to absorb the remainder — three
 * answers a clerk needs to see BEFORE they tell a parent what they will be paying. So the schedule
 * is shown, and only then is there a button that applies it.
 *
 * ⚠ IT SPLITS THE BALANCE, NOT THE TOTAL. A parent who has already paid ₹10,000 of ₹45,000 and asks
 * to spread the rest is the common case; the header says which number is being divided, because a
 * clerk reading "3 × ₹15,000" against a ₹45,000 invoice would otherwise assume the payment was lost.
 */
function SplitSection({ invoice, onSplit }: { invoice: InvoiceView; onSplit: (i: InvoiceView) => void }) {
  const existing = invoice.installments ?? [];
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState('3');
  const [firstDueOn, setFirstDueOn] = useState(() => new Date().toISOString().slice(0, 10));
  const [plan, setPlan] = useState<InstalmentPlanView | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ⚠ ANY EDIT DROPS THE PREVIEW. A schedule for "3" sitting above a box reading "6" is a schedule
  // somebody applies believing it is the one they are looking at.
  const edit = (fn: () => void) => { fn(); setPlan(null); setError(null); };

  const call = async (path: string, apply: boolean) => {
    setBusy(true); setError(null);
    try {
      const body = { count: Number(count), firstDueOn };
      const res = await api.post<InstalmentPlanView>(
        `/api/fees/invoices/${invoice.id}/instalments${path}`, body);
      setPlan(res);
      // ⚠ THE REFUSAL COMES BACK AS 200 WITH Ok:false ON PREVIEW and as an error on apply. Both
      // have to be surfaced, or a refused preview renders as an empty schedule.
      if (apply && res.ok) {
        const fresh = await api.get<InvoiceView>(`/api/fees/invoices/${invoice.id}`);
        onSplit(fresh);
        setOpen(false); setPlan(null);
      }
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'That could not be done.');
      setPlan(null);
    } finally { setBusy(false); }
  };

  return (
    <div style={{ marginBottom: 20, borderTop: '1px solid var(--line)', paddingTop: 16 }}
      data-testid="split-section">
      <div style={{ fontWeight: 600, marginBottom: 4 }}>Pay in instalments</div>

      {/* ⚠ AN EXISTING SCHEDULE IS SHOWN BEFORE ANY OFFER TO MAKE A NEW ONE. SetInstallments
          REPLACES rather than adds, so splitting a second time silently discards the schedule a
          parent has already been told about — and until the type declared this field, the drawer
          could not even tell the two cases apart. */}
      {existing.length > 0 && (
        <div data-testid="existing-schedule" style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 12, color: 'var(--ink-2)', marginBottom: 8 }}>
            Already split into {existing.length}. Splitting again replaces this schedule.
          </div>
          {existing.map((x) => (
            <div key={x.number} style={{ display: 'flex', justifyContent: 'space-between',
              padding: '4px 0', fontSize: 13.5 }}>
              <span style={{ color: 'var(--ink-2)' }}>{x.number}. {formatDate(x.dueDate)}</span>
              <span className="mono">{formatPaise(x.amountPaise)}</span>
            </div>
          ))}
        </div>
      )}

      {!open ? (
        <button className="btn" data-testid="split-open" style={{ width: '100%', justifyContent: 'center' }}
          onClick={() => setOpen(true)}>
          {existing.length > 0 ? 'Change the schedule' : `Split ${formatPaise(invoice.balancePaise)} into instalments`}
        </button>
      ) : (
        <>
          <div style={{ fontSize: 12, color: 'var(--ink-2)', marginBottom: 12 }}>
            Dividing the outstanding {formatPaise(invoice.balancePaise)}
            {invoice.paidPaise > 0 && <> — the {formatPaise(invoice.paidPaise)} already paid stays paid</>}.
          </div>

          {error && (
            <div className="pill pill-danger" data-testid="split-error"
              style={{ padding: '8px 12px', borderRadius: 'var(--r-md)', justifyContent: 'flex-start',
                marginBottom: 12, fontSize: 13 }}>{error}</div>
          )}

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
            <div style={{ flex: '1 1 110px', minWidth: 0 }}>
              <label className="label">How many</label>
              <input className="input mono" inputMode="numeric" value={count} data-testid="split-count"
                onChange={(e) => edit(() => setCount(e.target.value))} />
            </div>
            <div style={{ flex: '1 1 160px', minWidth: 0 }}>
              {/* ⚠ A DATE, NOT A DURATION. "Three instalments starting 10 July" is what a school says
                  at a counter; "over nine months" produces a first due date nobody chose. */}
              <label className="label">First one due</label>
              <input className="input" type="date" value={firstDueOn} data-testid="split-first-due"
                onChange={(e) => edit(() => setFirstDueOn(e.target.value))} />
            </div>
          </div>

          {plan && !plan.ok && (
            <div className="pill pill-danger" data-testid="split-refusal"
              style={{ padding: '8px 12px', borderRadius: 'var(--r-md)', justifyContent: 'flex-start',
                marginBottom: 12, fontSize: 13 }}>{plan.refusal}</div>
          )}

          {plan && plan.ok && (
            <div data-testid="split-preview" className="card"
              style={{ padding: 14, marginBottom: 12, background: 'var(--surface-2)' }}>
              {plan.lines.map((l) => (
                <div key={l.number} style={{ display: 'flex', justifyContent: 'space-between',
                  padding: '4px 0', fontSize: 13.5 }}>
                  <span style={{ color: 'var(--ink-2)' }}>{l.number}. {formatDate(l.dueOn)}</span>
                  <span className="mono">{formatPaise(l.amountPaise)}</span>
                </div>
              ))}
              {/* ⚠ THE SENTENCE THE CLERK READS OUT. plan.words is written for saying aloud to a
                  parent at a counter — the numbers above are for checking it. */}
              <div style={{ borderTop: '1px solid var(--line)', marginTop: 8, paddingTop: 9,
                fontSize: 13, color: 'var(--ink-2)' }} data-testid="split-words">
                {plan.words}
              </div>
            </div>
          )}

          {/* ⚠ THEY STACK RATHER THAN SQUEEZE. At 390px the drawer is about 338px wide, and two
              half-width buttons broke "Show the schedule" and "Use this schedule" across two lines
              each — 52px tall against a 36px single-line button elsewhere in the same drawer. A
              180px basis is wider than half, so on a phone they wrap to full width and stay on one
              line; on a desktop they sit side by side as before. */}
          <div style={{ display: 'flex', gap: 9, flexWrap: 'wrap' }}>
            <button className="btn" data-testid="split-preview-btn" disabled={busy}
              style={{ flex: '1 1 180px', minWidth: 0, justifyContent: 'center' }}
              onClick={() => void call('/preview', false)}>
              {busy ? 'Working…' : 'Show the schedule'}
            </button>
            {/* ⚠ DEAD UNTIL A SCHEDULE HAS BEEN SEEN AND ACCEPTED. The server rounds the last
                instalment and caps the count against school policy; applying blind means telling a
                parent one number and charging another. */}
            <button className="btn btn-accent" data-testid="split-apply"
              disabled={busy || plan === null || !plan.ok}
              style={{ flex: '1 1 180px', minWidth: 0, justifyContent: 'center' }}
              onClick={() => void call('', true)}>
              Use this schedule
            </button>
          </div>

          <button className="btn" style={{ width: '100%', justifyContent: 'center', marginTop: 9 }}
            onClick={() => { setOpen(false); setPlan(null); setError(null); }}>
            Cancel
          </button>
        </>
      )}
    </div>
  );
}



interface CreditAccount {
  studentId: string;
  balancePaise: number;
  receivedPaise: number;
  entries: { id: string; amountPaise: number; source: string; reason: string; recordedAt: string }[];
}

/**
 * ═══ MONEY THE SCHOOL IS ALREADY HOLDING FOR THIS FAMILY ═══
 *
 * ⚠ IT DOES NOT RENDER AT ALL WHEN THERE IS NOTHING TO SHOW. An empty "Credit: ₹0.00" on every
 * invoice in the school is a line four hundred clerks learn to skip, and the one day it says ₹5,000
 * they will skip it too.
 *
 * ⚠ APPLYING IT CREATES A PAYMENT WITH METHOD Credit, WHICH IS NOT CASH. No money arrives — it
 * arrived earlier and was banked then. A cash reconciliation must exclude those payments or it is
 * over by exactly the credit spent, which is why the method exists rather than the balance being
 * quietly adjusted.
 */
function CreditPanel({ invoice, onApplied }: { invoice: InvoiceView; onApplied: (i: InvoiceView) => void }) {
  const [account, setAccount] = useState<CreditAccount | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    api.get<CreditAccount>(`/api/fees/students/${invoice.studentId}/credit`)
      .then(setAccount)
      // ⚠ A FAILURE HERE IS SILENT ON PURPOSE, and it is the one silent catch in this drawer. The
      // credit ledger is optional — a school that has not enabled it gets an error on every invoice
      // open, and an error about a feature they do not use is noise that teaches them to ignore
      // errors that matter. Nothing is lost: with no panel, the payment box behaves exactly as
      // before.
      .catch(() => setAccount(null));
  }, [invoice.studentId]);

  useEffect(load, [load]);

  // ⚠ THE SHAPE IS CHECKED, NOT JUST THE PRESENCE, AND THIS CRASHED THE WHOLE DRAWER. `!account ||
  // account.balancePaise <= 0` looks like a guard and is not one: a 200 carrying `{}` — which is what
  // a server without the credit ledger, or a proxy returning an empty body, hands back — gives
  // `undefined <= 0` === false, so the panel rendered and then threw on `entries[0]`. The payment box
  // went with it, because a React error takes the whole subtree.
  //
  // ⚠ AN OPTIONAL FEATURE MUST NOT BE ABLE TO STOP A SCHOOL TAKING MONEY. That is the rule this line
  // is enforcing, and it is worth more than the panel it hides.
  if (typeof account?.balancePaise === 'number' && account.balancePaise > 0) { /* fall through */ } else return null;
  const entries = Array.isArray(account.entries) ? account.entries : [];

  // ⚠ CAPPED AT THE BILL. Setting more against it than is outstanding would leave the family paid up
  // and the school still holding the rest — which is where it already is, so the button would do
  // nothing except look like it had.
  const usable = Math.min(account.balancePaise, invoice.balancePaise);

  const apply = async () => {
    setBusy(true); setError(null);
    try {
      onApplied(await api.post<InvoiceView>(`/api/fees/invoices/${invoice.id}/apply-credit`,
        { amountPaise: usable }));
      load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not apply the credit.');
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="credit-panel" className="card" style={{ padding: 16, marginBottom: 16,
      background: 'var(--surface-2)', borderLeft: '4px solid var(--ok, #2F6B4F)' }}>
      <div style={{ fontWeight: 600, marginBottom: 3 }}>
        The school is holding {formatPaise(account.balancePaise)} for this family
      </div>
      {/* ⚠ WHERE IT CAME FROM, NOT JUST HOW MUCH. A parent asking in March why the school has ₹5,000
          of theirs deserves an answer better than "credit", and this is where a clerk reads it. */}
      {entries[0] && (
        <div style={{ fontSize: 12.5, color: 'var(--ink-3)', marginBottom: 10, lineHeight: 1.5 }}
          data-testid="credit-why">
          {entries[0].reason}
        </div>
      )}
      {error && (
        <div className="pill pill-danger" data-testid="credit-error"
          style={{ padding: '8px 12px', borderRadius: 'var(--r-md)', justifyContent: 'flex-start',
            marginBottom: 10, fontSize: 13 }}>{error}</div>
      )}
      <button className="btn btn-accent" data-testid="apply-credit" disabled={busy}
        style={{ width: '100%', justifyContent: 'center' }} onClick={() => void apply()}>
        {busy ? 'Applying…' : `Set ${formatPaise(usable)} against this bill`}
      </button>
      {/* ⚠ SAID WHEN THE CREDIT IS LARGER THAN THE BILL, because otherwise the button appears to
          spend less than the family has and a clerk goes looking for the difference. */}
      {account.balancePaise > invoice.balancePaise && (
        <div style={{ fontSize: 12.5, color: 'var(--ink-3)', marginTop: 8, lineHeight: 1.5 }}
          data-testid="credit-leftover">
          {formatPaise(account.balancePaise - invoice.balancePaise)} stays on their account for the
          next bill.
        </div>
      )}
    </div>
  );
}
